import { useState } from 'react';

/**
 * Allows for the progression through a series of named steps.
 * @param steps Steps to progress through. 'not-started' and 'complete' are automatically added at the start and end of the steps list.
 * @param continuous When true, if the last step has been reached, progressing will move to the 'not-started' step.
 */
export const useStepReducer = <T extends string>(steps: T[], continuous?: boolean) => {
    // TODO: This can use better generics
    const fullSteps = ['not-started', ...steps, 'complete'] as const;
    const [currentStep, setCurrentStep] = useState(0);

    return {
        /** The current step */
        current: fullSteps[currentStep] as typeof fullSteps[number],
        /**
         * Moves the current step to the next step.
         *  If the current step is complete and continuous is true, the next step will be not-started
         */
        next: () => {
            setCurrentStep((step) => {
                if (step + 1 < fullSteps.length) {
                    return step + 1;
                }
                else if (continuous) {
                    return 0;
                }
                else {
                    return step;
                }
            });
        },
        /** Resets the current step back to not-started */
        reset: () => setCurrentStep(0),
        /** Moves the current step to the step immediately following not-started */
        start: () => {
            setCurrentStep(1);
        },
        /** Moves the current step to the complete entry. */
        finish: () => {
            setCurrentStep(fullSteps.length - 1);
        },
        /** True if the steps have started but not completed */
        processing: currentStep !== 0 && currentStep !== fullSteps.length - 1,
        stepCompleted: (step: typeof fullSteps[number]) => fullSteps.indexOf(step) < currentStep,
    };
};
